import React from 'react';
import styled from 'styled-components';
import { SxProps, Theme } from '@mui/material';
import Button from '../Button';
import { Flex } from '../Flex';
import { fontSize, fontWeight, radius, space } from '../../style/utils';

const FileUploadWrapper = styled(Flex)`
  position: relative;
  height: 100%;
  :hover {
    background: ${({ theme }) => theme.palette.surface.track};
  }
`;

const FileInputWrapper = styled(Flex)`
  align-items: center;
  border: 1px solid ${({ theme }) => theme.palette.border.controlStrong};
  border-radius: ${radius('lg')};
  height: 40px;
  overflow: hidden;
`;

export const FileInputLabel = styled.input`
  width: 100%;
  font-size: ${fontSize('base')} !important;
  padding: ${space(0, 'mdPlus')};
  border: none;
  background: none;
  color: ${({ theme }) => theme.palette.content.primary};
  font-weight: ${({ value, theme }) =>
    value ? theme.typography.fontWeightBold : fontWeight('regular')};
`;

export const FileButton = styled(Button)<{ component?: string }>`
  &.MuiButton-root {
    height: 100%;
    width: 90x;
    box-shadow: none;
    background: none;
    color: ${({ theme }) => theme.palette.content.primary};
    overflow: hidden;
    border: 1px solid ${({ theme }) => theme.palette.border.strong};
    border-radius: ${radius(0, 'lg', 'lg', 0)};
    :hover {
      background: none;
      cursor: pointer;
    }
  }
`;

export const FileInput = styled.input`
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
`;

export type SimpleFile = { name: string; content: string };

type InputValue = SimpleFile | File | null;

type Props = {
  value: InputValue;
  onChange: (value: InputValue) => void;
  placeholder: string;
  sx?: SxProps<Theme>;
  storeFileObject?: boolean;
};

const FileUploadInput = React.forwardRef(
  (
    {
      value = { name: '', content: '' },
      onChange,
      placeholder,
      sx,
      storeFileObject = false,
    }: Props,
    ref: any,
  ) => {
    const readText = async (event: any) => {
      event.preventDefault();

      if (!event.target.files || !event.target.files[0]) {
        return;
      }

      if (storeFileObject) {
        onChange(event.target.files[0]);
        return;
      }

      const reader = new FileReader();

      reader.onload = async (e: any) => {
        const name = event.target.files[0]?.name;
        const content = e.target.result;

        onChange({ name, content });
      };

      reader.readAsText(event.target.files[0]);
    };

    const displayName =
      value instanceof File
        ? value.name
        : value && typeof value === 'object' && 'name' in value
        ? value.name
        : '';

    return (
      <FileInputWrapper ref={ref} sx={{ width: '100%', ...sx }}>
        <FileInputLabel
          disabled
          placeholder={placeholder}
          value={displayName}
        />
        <FileUploadWrapper>
          <FileButton disableElevation variant="outlined" component="label">
            Browse
            <FileInput
              type="file"
              hidden
              onChange={(e) => readText(e)}
              onBlur={(e) => e.preventDefault()}
            />
          </FileButton>
        </FileUploadWrapper>
      </FileInputWrapper>
    );
  },
);

export default FileUploadInput;
